import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, Button, Typography, Grid, Paper, List, ListItem, ListItemAvatar, 
  ListItemText, Avatar, IconButton, Dialog, DialogActions, DialogContent, 
  DialogContentText, DialogTitle, CircularProgress, Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { ArrowForward, Delete, Refresh, ArrowBack } from '@mui/icons-material';
import API_BASE_URL from '../config';
import ControlPasswordProtected from '../components/ControlPasswordProtected';
import ca from 'date-fns/esm/locale/ca';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const FileManagementDeviceContent = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [storageRemaining, setStorageRemaining] = useState(null);
  const [storageRemainingLoading, setStorageRemainingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#ffffff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#000000');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  const [userMediaImages, setUserMediaImages] = useState({});
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [deviceMediaImages, setDeviceMediaImages] = useState({});
  const [isLoadingDeviceImages, setIsLoadingDeviceImages] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [userMediaCount, setUserMediaCount] = useState(0);
  const [deviceMediaCount, setDeviceMediaCount] = useState(0);
  const [packageMediaAllowance, setPackageMediaAllowance] = useState(0);


  useEffect(() => {
    const interval = setInterval(() => {
      setPrimaryColor(localStorage.getItem('primaryColor') || '#ffffff');
      setSecondaryColor(localStorage.getItem('secondaryColor') || '#000000');
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const textColor = darkMode ? '#ffffff' : '#000000';

  useEffect(() => {
    fetchPackageAllowance(deviceId);
    fetchDeviceDetails(deviceId);
    fetchStorageRemaining(deviceId);
    fetchUserMediaList();
    fetchDeviceMediaList(deviceId);
  }, [deviceId]);

  const fetchPackageAllowance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/package/media`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setPackageMediaAllowance(data);

    } catch (error) {
      console.error('Error fetching user package:', error);
      setError('Failed to fetch user package. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/details-file/${deviceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setDevice(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching device details:', error);
      setError('Failed to fetch device details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  

  const fetchStorageRemaining = async (deviceId) => {
    setStorageRemainingLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/device/device-storage-remaining/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setStorageRemaining(data.storageRemaining || 'N/A');
    } catch (error) {
      console.error('Error fetching device storage:', error);
    } finally {
      setStorageRemainingLoading(false);
    }
  };

  const fetchUserMediaList = useCallback(async () => {
    setIsLoadingImages(true);
    try {
      const response = await fetch(`${API_BASE_URL}/device/media-list`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      const mediaFiles = await response.json();
      setUserMediaCount(mediaFiles.length);
      const imagePromises = mediaFiles.map(async (file) => {
        const imageResponse = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(file.file_name)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!imageResponse.ok) {
          console.error(`Failed to fetch image for ${file.file_name}`);
          return null;
        }
        const blob = await imageResponse.blob();
        return { name: file.file_name, url: URL.createObjectURL(blob), size: file.file_size };
      }
    );


      const images = await Promise.all(imagePromises);
      const imageMap = images.reduce((acc, img) => {
        if (img) {
          acc[img.name] = {
            url: img.url,
            size: img.size
          };
        }
        return acc;
      }, {});
      setUserMediaImages(imageMap);
    } catch (error) {
      console.error('Error fetching user media images:', error);
    } finally {
      setIsLoadingImages(false);
    }
  }, []);

  const fetchDeviceMediaList  = async (deviceId) => {
    setIsLoadingDeviceImages(true);
    try {
      const response = await fetch(`${API_BASE_URL}/device/device-media-list/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      const mediaFiles = await response.json();
      const imagePromises = mediaFiles.map(async (file) => {
        const imageResponse = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(file.file_name)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!imageResponse.ok) {
          console.error(`Failed to fetch image for ${file.file_name}`);
          return null;
        }
        const blob = await imageResponse.blob();
        return { name: file.file_name, url: URL.createObjectURL(blob), size: file.file_size };
      });

      const images = await Promise.all(imagePromises);
      const imageMap = images.reduce((acc, img) => {
        if (img) {
          acc[img.name] = {
            url: img.url,
            size: img.size
          };
        }
        return acc;
      }, {});
      setDeviceMediaCount(Object.keys(imageMap).length);
      setDeviceMediaImages(imageMap);
    } catch (error) {
      console.error('Error fetching user media images:', error);
    } finally {
      setIsLoadingDeviceImages(false);
    }
  };

  const generateSampleFiles = (prefix) => 
    Array.from({ length: 20 }, (_, i) => ({
      id: prefix + (i + 1),
      name: `${prefix}_image${i + 1}.jpg`,
      size: 1024,
      preview: `url_to_preview_${prefix}${i + 1}`
    }));

  const [serverFiles, setServerFiles] = useState(generateSampleFiles('server'));
  const [deviceFiles, setDeviceFiles] = useState(generateSampleFiles('device'));

  const handleBack = () => {
    navigate('/file-management');
  };

  const handleSendToDevice = async () => {
    setLoading(true);
    if (selectedFile && selectedFileType === 'server') {
      if (selectedFile.name) {
        try {
          const response = await fetch(`${API_BASE_URL}/device/send-file/${deviceId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ fileName: selectedFile.name })
          });
  
          if (response.status === 200) {
            const result = await response.json();
            console.log('File send result:', result);

            if (response.status === 401) {
              navigate('/unauthorized');
              return;
            }
  
            // Remove the deleted file from the deviceFiles state
            setSelectedFile(null);
  
            // Show success message
            setSnackbarMessage('File successfully sent');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
  
            // Refresh the page
            handleRefresh();
          } else {
            throw new Error('Send operation did not complete');
          }
        } catch (error) {
          console.error('Error sending file:', error);
          
          // Show error message
          setSnackbarMessage('Send operation did not complete. Please try again.');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    }
    setLoading(false);
  };

  const handleDeleteFromDevice = () => {
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (selectedFile) {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/device/delete-file/${deviceId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ fileName: selectedFile.name })
        });

        if (response.status === 401) {
          navigate('/unauthorized');
          return;
        }

        if (response.status === 200) {
          const result = await response.json();
          console.log('File deletion result:', result);

          // Remove the deleted file from the deviceFiles state
          setDeviceFiles(prevFiles => prevFiles.filter(file => file.name !== selectedFile.name));
          setSelectedFile(null);
          setOpenDeleteDialog(false);

          // Show success message
          setSnackbarMessage('File successfully deleted');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);

          // Refresh the page
          handleRefresh();
        } else {
          throw new Error('Delete operation did not complete');
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        
        // Show error message
        setSnackbarMessage('Delete operation did not complete. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleFileSelect = (file, fileType) => {
    setSelectedFile(file);
    setSelectedFileType(fileType);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    fetchDeviceDetails(deviceId);
    fetchUserMediaList();
    fetchDeviceMediaList(deviceId);
    setIsLoading(false);
  };

  const formatFileSize = (size) => {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    }
  };

  const FileList = useMemo(() => ({ files, onSelect, selectedFile, fileType, isLoading }) => (
    <List sx={{ 
      height: '100%', 
      overflowY: 'auto',
      '& .MuiListItem-root:nth-of-type(odd)': {
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
      }
    }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress sx={{ color: textColor }} />
        </Box>
      ) : (
        Object.entries(files).map(([fileName, fileInfo]) => (
          <ListItem 
            key={fileName} 
            onClick={() => onSelect({ name: fileName, ...fileInfo }, fileType)}
            sx={{ 
              borderBottom: '1px solid',
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
              },
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
            }}
          >
            <ListItemAvatar sx={{ width: '128px', height: '96px' }}>
              <Avatar 
                src={fileInfo.url} 
                variant="square" 
                sx={{ width: '128px', height: '96px', objectFit: 'cover' }}
              />
            </ListItemAvatar>
            <ListItemText 
              primary={fileName} 
              secondary={fileInfo.size ? formatFileSize(fileInfo.size) : ''}
              primaryTypographyProps={{ 
                style: { 
                  color: selectedFile && selectedFile.name === fileName 
                    ? '#4CAF50'
                    : textColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                } 
              }}
              secondaryTypographyProps={{
                style: { color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }
              }}
              sx={{ flex: 1 }}
            />
          </ListItem>
        ))
      )}
    </List>
  ), [darkMode, textColor, isLoadingImages]);


  return (
    <Box sx={{ p: 3, backgroundColor: primaryColor, minHeight: '100vh', color: textColor, overflow: 'hidden' }}>
      <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ color: textColor, backgroundColor: secondaryColor }}>
        Back
      </Button>
      
      <Box sx={{ mt: 2, mb: 3, p: 2, maxWidth: '100%',  backgroundColor: secondaryColor, borderRadius: 1 }}>
      <Typography variant="h6">
        {loading ? <CircularProgress size={20} sx={{ color: textColor }} /> : device && device.device_name}
      </Typography>
      <Typography sx={{ color: textColor }}>
        Resolution: {loading ? <CircularProgress size={20} sx={{ color: textColor }} /> : device && device.screen_width + ` x ` + device.screen_height} Pixels ({device && device.color_order})
      </Typography>
      <Typography sx={{ color: textColor }}>
        Package Allowance: {loading ? <CircularProgress size={20} sx={{ color: textColor }} /> : packageMediaAllowance} Files
      </Typography>
      <Typography variant="body2" color="warning" sx={{ mt: 1, mb: 0, maxWidth: '100%', whiteSpace: 'nowrap' }}>
        Please make sure the resolution of media matches the resolution of the corresponding device model to avoid display error.
      </Typography>
    </Box>
      <Grid container spacing={2} sx={{ height: 'calc(100vh - 250px)' }}>
        <Grid item xs={5} sx={{ height: '100%' }}>
          <Paper sx={{ p: 2, height: '100%', backgroundColor: secondaryColor, overflow: 'auto' }}>
            <Typography variant="h6" sx={{ color: textColor, fontWeight: 'bold', mb: 2 }}>Server Files ({userMediaCount} Files)</Typography>
            <FileList 
              files={userMediaImages} 
              onSelect={handleFileSelect}
              selectedFile={selectedFile}
              fileType="server"
              isLoading={isLoadingImages}
            />
          </Paper>
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton 
            onClick={handleSendToDevice} 
            disabled={!selectedFile || selectedFileType !== 'server' || isLoading || deviceMediaCount > packageMediaAllowance}
            sx={{ 
              color: textColor, 
              '&.Mui-disabled': { color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' },
              mb: 2
            }}
          >
            <ArrowForward sx={{ fontSize: 40 }} />
          </IconButton>
          <IconButton 
            onClick={handleDeleteFromDevice} 
            disabled={!selectedFile || selectedFileType !== 'device' || isLoading}
            sx={{ 
              color: textColor, 
              '&.Mui-disabled': { color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' },
              mb: 2
            }}
          >
            <Delete sx={{ fontSize: 40 }} />
          </IconButton>
          <IconButton 
            onClick={handleRefresh} 
            sx={{ 
              color: textColor,
              '&.Mui-disabled': { color: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }
            }}
          >
            <Refresh sx={{ fontSize: 40 }} />
          </IconButton>
          {isLoading && <CircularProgress sx={{ color: textColor, mt: 2 }} />}
        </Grid>
        <Grid item xs={5} sx={{ height: '100%' }}>
          <Paper sx={{ p: 2, height: '100%', backgroundColor: secondaryColor, overflow: 'auto' }}>
            <Typography variant="h6" sx={{ color: textColor, fontWeight: 'bold', mb: 2 }}>Device Files ({deviceMediaCount} Files)</Typography>
            {deviceMediaCount > packageMediaAllowance && (
              <Typography variant="body2" color="warning" sx={{ mt: 0, mb: 0, whiteSpace: 'nowrap' }}>
                You have exceeded the package file allowance.
              </Typography>
            )}
            <FileList 
              files={deviceMediaImages} 
              onSelect={handleFileSelect}
              selectedFile={selectedFile}
              fileType="device"
              isLoading={isLoadingDeviceImages}
            />
          </Paper>
        </Grid>
      </Grid>
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          style: {
            backgroundColor: primaryColor,
            color: textColor,
          },
        }}
      >
        <DialogTitle sx={{ color: `${textColor} !important` }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: `${textColor} !important` }}>
            Are you sure you want to delete the file "{selectedFile?.name}" from device?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: `${textColor} !important` }}>Cancel</Button>
          <Button onClick={confirmDelete} sx={{ color: 'error.main' }}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
const FileManagementDevice = ({ deviceId }) => {
  const ProtectedComponent = ControlPasswordProtected(FileManagementDeviceContent);
  return <ProtectedComponent deviceId={deviceId} />;
};

export default FileManagementDevice;


